/**
 * Single source of truth for store metadata across the DomiOps app.
 *
 * These exports are LIVE containers, not frozen constants. On session bootstrap
 * `StoreMetaContext` fetches the tenant-aware GET /api/store-codes and calls
 * `hydrateStoreMeta(...)`, which mutates these objects/arrays IN PLACE so every
 * module that imported them (there are ~25) instantly sees the caller's own
 * store universe — the 5 real stores for real sessions, the 5 fictional demo
 * stores (London / Manchester / Liverpool / Birmingham / Newcastle) for the
 * "Explore the Demo" account. This is what keeps demo mode from ever rendering
 * a real store code, name or colour.
 *
 * Defaults below are the REAL stores so anonymous / pre-hydration renders are
 * unchanged; they are overwritten the moment the API responds.
 */

export const STORE_CODES = ["FAL", "CUR", "LIN", "GRA", "WIN"];

export const STORE_NAMES = {
  FAL: "Falkirk",
  CUR: "Currie",
  LIN: "Linlithgow",
  GRA: "Grangemouth",
  WIN: "Winchburgh",
};

export const STORE_FULL_NAMES = {
  FAL: "Falkirk",
  CUR: "Edinburgh · Currie",
  LIN: "Linlithgow",
  GRA: "Falkirk · Grangemouth",
  WIN: "Winchburgh",
};

// Brand accent per store — used by Domi-Grill, charts, etc.
export const STORE_COLORS = {
  FAL: "#E11D48",
  CUR: "#2563EB",
  LIN: "#16A34A",
  GRA: "#F59E0B",
  WIN: "#7C3AED",
};

export const labelFor = (code) => STORE_NAMES[code] || code;
export const fullLabelFor = (code) => STORE_FULL_NAMES[code] || code;
export const colorFor = (code) => STORE_COLORS[code] || "#64748b";

// Friendly name of the Nth store in the CURRENT tenant's universe — used for
// paste-box placeholder examples so they never show a real store name in demo
// mode. Falls back to the code, then a generic label.
export const exampleName = (i = 0) => STORE_NAMES[STORE_CODES[i]] || STORE_CODES[i] || "Store";

// ── Runtime hydration ────────────────────────────────────────────────────────
const _listeners = new Set();

/** Subscribe to metadata changes (fires after each hydrate). Returns unsubscribe. */
export function subscribeStoreMeta(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function _replaceObj(target, next) {
  if (!next || typeof next !== "object") return;
  Object.keys(target).forEach((k) => delete target[k]);
  Object.entries(next).forEach(([k, v]) => { target[k] = v; });
}

/** Overwrite the live registries in place from GET /api/store-codes. */
export function hydrateStoreMeta(data) {
  if (!data) return;
  const { store_codes, names, full_names, colors } = data;
  if (Array.isArray(store_codes) && store_codes.length) {
    STORE_CODES.length = 0;
    store_codes.forEach((c) => STORE_CODES.push(c));
  }
  _replaceObj(STORE_NAMES, names);
  _replaceObj(STORE_FULL_NAMES, full_names);
  _replaceObj(STORE_COLORS, colors);
  _listeners.forEach((fn) => { try { fn(); } catch { /* no-op */ } });
}
